import React, { useState, useEffect, useCallback } from "react";
import {
  DivTag,
  InputWithLabel,
  SelectWithLabelOption,
  Button,
} from "../../Components/Tags/Tags";
import FormValidation from "../../Components/FormValidation/FormValidation";
import Header from "../../Components/Header";
import Seo from "../../Components/SeoComponent/Seo";
import { WalletReportService } from "../../Services/WalletReport/walletReport";
import { FaInbox } from "react-icons/fa";
import PaginationData from "../../Components/PaginationData";
import { FaIdBadge } from "react-icons/fa";

const BusinessWalletStructure = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [lastPage, setLastPage] = useState(1);
  const [walletData, setWalletData] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [totalBalance, setTotalBalance] = useState(0);
  const [pagination, setPagination] = useState({
    total: 0,
    per_page: 1,
    current_page: 1,
    last_page: 1,
  });

  const initialState = {
    from_date: "",
    to_date: "",
    total_data: "10",
    status: "",
  };

  const validate = () => {
    let errors = {};
    return errors;
  };

  const { values, errors, handleChange, handleBlur } = FormValidation(
    initialState,
    validate,
  );

  const fetchWalletData = useCallback(
    async (page = 1) => {
      try {
        const params = {
          from_date: values.from_date,
          to_date: values.to_date,
          status: values.status,
          page,
          total_data: itemsPerPage,
        };
        const response = await WalletReportService(params);

        if (response.data && Array.isArray(response.data.data)) {
          setWalletData(response.data.data);
          const totalAmounts = walletData?.reduce(
            (sum, item) => sum + Number(item?.amount || 0),
            0,
          );
          const totalBalances = walletData?.reduce(
            (sum, item) => sum + Number(item?.balance_after || 0),
            0,
          );
          setTotalAmount(totalAmounts);
          setTotalBalance(totalBalances);

          setPagination(response.data.pagination || {});
          setCurrentPage(response.data.pagination.current_page || 1);
          setLastPage(response.data.pagination.last_page || 1);
        } else {
          console.error("Data format error:", response.data.data);
          setWalletData([]);
          setPagination({});
        }
      } catch (error) {
        console.error("Error fetching Wallet Report data:", error);
        setWalletData([]);
        setPagination({});
      }
    },
    [values, itemsPerPage],
  );

  useEffect(() => {
    fetchWalletData(currentPage);
  }, [fetchWalletData, currentPage]);

  const handleSearchClick = () => {
    setCurrentPage(1);
    fetchWalletData(1);
  };

  function handlePage(page) {
    setCurrentPage(page);
  }

  return (
    <DivTag className="container mx-auto">
      <Seo />
      <Header heading="Wallet Report" />
      <DivTag className="flex sm:justify-center flex-wrap gap-4">
        <InputWithLabel
          DivclassName="w-full md:w-1/6 px-2"
          label="From Date"
          type="date"
          name="from_date"
          id="from_date"
          value={values.from_date}
          onChange={handleChange}
          onBlur={handleBlur}
          className={`border rounded-md py-2 px-3 ${errors.from_date ? "border-red-500" : "border-blue-600"}`}
          error={errors.from_date}
        />
        <InputWithLabel
          DivclassName="w-full md:w-1/6 px-2"
          label="To Date"
          type="date"
          name="to_date"
          id="to_date"
          value={values.to_date}
          onChange={handleChange}
          onBlur={handleBlur}
          className={`border rounded-md py-2 px-3 ${errors.to_date ? "border-red-500" : "border-blue-600"}`}
          error={errors.to_date}
        />
        <SelectWithLabelOption
          DivclassName="w-full md:w-1/4 px-2"
          label="Records Per Page"
          name="total_data"
          id="total_data"
          value={values.total_data}
          onChange={(e) => {
            handleChange(e);
            setItemsPerPage(Number(e.target.value));
          }}
          className="border rounded-md py-2 px-3 border-blue-600"
        >
          <option value="10">10</option>
          <option value="30">30</option>
          <option value="50">50</option>
          <option value="100">100</option>
        </SelectWithLabelOption>

        <DivTag className="flex justify-center mt-1">
          <Button
            type="button"
            onClick={handleSearchClick}
            className="bg-gradient-to-r from-cyan-300 to-blue-900 text-white px-2 py-1 rounded disabled:opacity-50 border-sky-300 !h-8 !mt-4 shadow-lg shadow-blue-500/50"
          >
            Search
          </Button>
        </DivTag>
      </DivTag>

      {walletData && walletData.length > 0 ? (
        <>
          <div className="overflow-x-auto mt-2 hidden md:block">
            <table id="table" className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr className="bg-blue-700 text-white">
                  <th className="border-2 p-2">SNo.</th>
                  <th className="border-2">User ID</th>
                  <th className="border-2">Account Type</th>
                  <th className="border-2">Amount</th>
                  <th className="border-2 hidden">Avl. Balance</th>
                  <th className="border-2 hidden">Gst</th>
                  <th className="border-2 hidden">Gst_Value</th>
                  <th className="border-2">Type</th>
                  <th className="border-2">Service Name</th>
                  <th className="border-2">TXN Type</th>
                  <th className="border-2 hidden">Env.</th>
                  <th className="border-2">Remarks</th>
                  <th className="border-2">Date</th>
                  <th className="border-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {walletData?.map((item, index) => (
                  <tr
                    key={(currentPage - 1) * itemsPerPage + index + 1}
                    className="text-center"
                  >
                    <td className="border shadow-inner shadow-blue-400 p-1">
                      {(currentPage - 1) * itemsPerPage + index + 1}
                    </td>
                    <td className="border shadow-inner shadow-blue-400 p-1">
                      <span className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 font-semibold rounded-full px-0 py-0 shadow-md transition duration-300 ease-in-out hover:scale-105 hover:shadow-lg">
                        <FaIdBadge className="text-green-600" />
                        {item?.user?.user_id || "N/A"}
                      </span>
                    </td>
                    <td className="border shadow-inner shadow-blue-400 p-1 transition duration-300 ease-in-out hover:scale-105 text-xs">
                      {item?.transaction_type || "N/A"}
                    </td>
                    <td
                      className={`border font-semibold rounded px-1 py-1 transition duration-300 ease-in-out hover:scale-105 ${
                        item?.factor === "Cr"
                          ? "text-green-600 bg-green-50 shadow-inner shadow-green-400"
                          : item?.factor === "Dr"
                            ? "text-red-600 bg-red-50 shadow-inner shadow-red-400"
                            : ""
                      }`}
                    >
                      ₹
                      {item?.amount?.toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
                      }) || "N/A"}
                    </td>

                    <td
                      className={`border font-semibold rounded px-1 py-1 transition duration-300 hidden ease-in-out hover:scale-105 ${
                        item?.factor === "Cr"
                          ? "text-green-600 bg-green-50 shadow-inner shadow-green-400"
                          : item?.factor === "Dr"
                            ? "text-red-600 bg-red-50 shadow-inner shadow-red-400"
                            : ""
                      }`}
                    >
                      ₹
                      {item?.balance_after?.toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
                      }) || "N/A"}
                    </td>

                    <td className="border shadow-inner shadow-blue-400 p-1 transition duration-300 ease-in-out hover:scale-105 hidden">
                      {item?.gst_percentage ? `${item.gst_percentage}%` : "N/A"}
                    </td>
                    <td className="border shadow-inner shadow-blue-400 p-1 transition duration-300 ease-in-out hover:scale-105 hidden">
                      {item?.gst_value ? `${item.gst_value}` : "N/A"}
                    </td>

                    <td
                      className={`border shadow-inner p-1 transition duration-300 ease-in-out hover:scale-105 ${
                        item?.factor === "Dr"
                          ? "text-red-500 shadow-red-400"
                          : item?.factor === "Cr"
                            ? "text-green-500 shadow-green-400"
                            : ""
                      }`}
                    >
                      {item?.factor}
                    </td>
                    <td className="border shadow-inner shadow-blue-400 p-1 transition duration-300 ease-in-out hover:scale-105 text-xs">
                      {item?.source?.service_name || "N/A"}
                    </td>
                    <td className="border shadow-inner shadow-blue-400 p-1 transition duration-300 ease-in-out hover:scale-105 text-xs">
                      {item?.txn_type || "N/A"}
                    </td>

                    <td className="border shadow-inner shadow-blue-400 p-1 transition duration-300 ease-in-out hover:scale-105 text-xs hidden">
                      {item?.user?.api_keys?.environment || "N/A"}
                    </td>
                    <td className="border shadow-inner shadow-blue-400 p-1 transition duration-300 ease-in-out hover:scale-105">
                      {item?.narration === "No remarks provided"
                        ? "N/A"
                        : item?.narration}
                    </td>
                    <td className="border shadow-inner shadow-blue-400 p-1 transition duration-300 ease-in-out hover:scale-105 text-xs">
                      {item?.transaction_date
                        ? new Date(item.transaction_date).toLocaleString(
                            "en-US",
                            {
                              weekday: "short",
                              year: "numeric",
                              month: "short",
                              day: "2-digit",
                              hour: "numeric",
                              minute: "2-digit",
                              hour12: true,
                            },
                          )
                        : "N/A"}
                    </td>

                    <td className="border shadow-inner shadow-blue-400 p-1 transition duration-300 ease-in-out hover:scale-105  ">
                      <button
                        className={`px-1 py-1 text-white rounded ${
                          item.status.value === "approved"
                            ? "bg-green-600 first-letter:uppercase text-xs"
                            : item?.status?.value === "pending"
                              ? "bg-yellow-500 first-letter:uppercase text-xs"
                              : "bg-red-500 first-letter:uppercase text-xs"
                        }`}
                      >
                        {item?.status?.value || "N/A"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Card view for mobile */}
          <div className="md:hidden space-y-4 p-2">
            {walletData.map((item, index) => (
              <div
                key={index}
                className="rounded-xl shadow-md border border-blue-200 p-2 bg-white transition transform hover:scale-[1.01]"
              >
                <div className="flex justify-between text-sm text-gray-600 font-semibold mb-1">
                  <span>SNo:</span>
                  <span>{(currentPage - 1) * itemsPerPage + index + 1}</span>
                </div>

                <div className="flex justify-between">
                  <div className="text-blue-700 font-semibold mb-1">
                    <FaIdBadge className="inline mr-1 text-green-600" />
                    {item?.user?.user_id || "N/A"}
                  </div>

                  <div
                    className={`text-sm font-bold ${item?.factor === "Cr" ? "text-green-600" : "text-red-600"}`}
                  >
                    ₹
                    {item?.amount?.toLocaleString("en-IN", {
                      minimumFractionDigits: 2,
                    }) || "N/A"}{" "}
                    ({item?.factor})
                  </div>
                </div>

                <div className="text-gray-700 text-sm mt-1">
                  <strong>Service:</strong>{" "}
                  {item?.source?.service_name || "N/A"}
                </div>

                <div className="flex justify-between">
                  <div className="text-gray-700 text-sm mt-1">
                    <strong>Remarks:</strong>{" "}
                    {item?.remarks === "No remarks provided"
                      ? "N/A"
                      : item?.remarks}
                  </div>

                  <div className="text-gray-600 text-xs mt-1">
                    <strong>Date:</strong>{" "}
                    {item?.transaction_date
                      ? new Date(item.transaction_date).toLocaleString(
                          "en-US",
                          {
                            weekday: "short",
                            year: "numeric",
                            month: "short",
                            day: "2-digit",
                            hour: "numeric",
                            minute: "2-digit",
                            hour12: true,
                          },
                        )
                      : "N/A"}
                  </div>
                </div>

                <div className="flex justify-center mt-1">
                  <span
                    className={`inline-block px-2 py-1 rounded text-white text-xs capitalize ${
                      item?.status?.value === "approved"
                        ? "bg-green-600"
                        : item?.status?.value === "pending"
                          ? "bg-yellow-500"
                          : "bg-red-500"
                    }`}
                  >
                    {item?.status?.value || "N/A"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </>
      ) : (
        <div className="flex items-center mt-4 gap-4 text-xl text-neutral-600 justify-center">
          <FaInbox className="text-blue-700" />
          <p className="flex justify-center items-center">Data not found</p>
        </div>
      )}

      <DivTag className="flex p-4 justify-center mt-4">
        <PaginationData
          currentPage={currentPage}
          lastPage={lastPage}
          handlePage={handlePage}
        />
      </DivTag>
    </DivTag>
  );
};

export default BusinessWalletStructure;
